/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright (c) 2026 Steven Warehouse Project. All Rights Reserved.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy-load all pages for code splitting + Suspense loading animation
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Sources = lazy(() => import('./pages/Sources'));
const SourcesMarketplace = lazy(() => import('./pages/SourcesMarketplace'));
const SourceSetup = lazy(() => import('./pages/SourceSetup'));
const Destinations = lazy(() => import('./pages/Destinations'));
const Connections = lazy(() => import('./pages/Connections'));
const Users = lazy(() => import('./pages/Users'));
const OAuthAuthorize = lazy(() => import('./pages/OAuthAuthorize'));
const OAuthSuccess = lazy(() => import('./pages/OAuthSuccess'));
const OAuthError = lazy(() => import('./pages/OAuthError'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const GuestRadar = lazy(() => import('./pages/GuestRadar'));
const ResearchAgent = lazy(() => import('./pages/ResearchAgent'));
const EpisodeSearch = lazy(() => import('./pages/EpisodeSearch'));
const PodcastInsights = lazy(() => import('./pages/PodcastInsights'));
const AudienceInsights = lazy(() => import('./pages/AudienceInsights'));
const ProfileExplorer = lazy(() => import('./pages/ProfileExplorer'));
const Segments = lazy(() => import('./pages/Segments'));
const YouTubeInsights = lazy(() => import('./pages/YouTubeInsights'));
const SocialInsights = lazy(() => import('./pages/SocialInsights'));
const EpisodeInsights = lazy(() => import('./pages/EpisodeInsights'));
const LinkManager = lazy(() => import('./pages/LinkManager'));
const McpCredentials = lazy(() => import('./pages/McpCredentials'));
const CommercialPacing = lazy(() => import('./pages/CommercialPacing'));
const MonthlyReport = lazy(() => import('./pages/MonthlyReport'));
const ThumbnailGenerator = lazy(() => import('./pages/ThumbnailGenerator'));

function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-surface-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-surface-700 border-t-primary-500 rounded-full animate-spin" />
      </div>
    }>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/oauth/authorize/:linkId" element={<OAuthAuthorize />} />
      <Route path="/oauth/success" element={<OAuthSuccess />} />
      <Route path="/oauth/error" element={<OAuthError />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Sources (new) */}
        <Route path="sources" element={<Sources />} />
        <Route
          path="sources/marketplace"
          element={
            <ProtectedRoute requireAdmin>
              <SourcesMarketplace />
            </ProtectedRoute>
          }
        />
        <Route
          path="sources/setup/:sourceTypeId"
          element={
            <ProtectedRoute requireAdmin>
              <SourceSetup />
            </ProtectedRoute>
          }
        />
        
        {/* Destinations */}
        <Route
          path="destinations"
          element={
            <ProtectedRoute requireAdmin>
              <Destinations />
            </ProtectedRoute>
          }
        />
        
        {/* Connections (source → destination mappings) */}
        <Route
          path="connections"
          element={
            <ProtectedRoute requireAdmin>
              <Connections />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="users"
          element={
            <ProtectedRoute requireAdmin>
              <Users />
            </ProtectedRoute>
          }
        />
        
        {/* Insights */}
        <Route path="guest-radar" element={<GuestRadar />} />
        <Route path="research-agent" element={<ResearchAgent />} />
        <Route path="episode-search" element={<EpisodeSearch />} />
        <Route path="podcast-insights" element={<PodcastInsights />} />
        <Route path="audience-insights" element={<AudienceInsights />} />
        <Route path="profile-explorer" element={<ProfileExplorer />} />
        <Route path="segments" element={<Segments />} />
        <Route path="youtube-insights" element={<YouTubeInsights />} />
        <Route path="social-insights" element={<SocialInsights />} />
        <Route path="episode-insights" element={<EpisodeInsights />} />
        <Route path="commercial-pacing" element={<CommercialPacing />} />
        <Route path="monthly-report" element={<MonthlyReport />} />
        <Route path="link-manager" element={<LinkManager />} />
        <Route path="thumbnail-generator" element={<ThumbnailGenerator />} />
        <Route
          path="mcp-credentials"
          element={
            <ProtectedRoute requireAdmin>
              <McpCredentials />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default App;
